import { View } from 'foundations-components/components/View';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { sortProductLines } from 'pricing-pages-lib/utils/sortingUtils';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { MARKETING_PLUS_SUITE } from 'self-service-api/constants/BundleTypes';
import { BUNDLE } from 'self-service-api/constants/UpgradeProducts';
import BannerSection from '../components/banners/BannerSection';
import IntegrationsSection from '../components/common/IntegrationsSection';
import PhoneSupportFooter from '../components/common/PhoneSupportFooter';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import { CreditsCallout } from '../components/common/credits-callout/CreditsCallout';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import ErrorBoundary from '../components/error-boundary/ErrorBoundary';
import { ERROR_SEVERITY } from '../components/error-boundary/error-severity';
import withErrorBoundary from '../components/error-boundary/withErrorBoundary';
import { getI18nKeyOverrideForProductCard } from '../components/merch-card/I18nUtils';
import { ProductCard } from '../components/merch-card/cards/product-card/ProductCard';
import MobileBundleFeatureTable from '../components/mobile/MobileBundleFeatureTable';
import MobileMerchCardSelectorButtons from '../components/mobile/MobileMerchCardSelectorButtons';
import MobileCardWrapper from '../components/mobile/containers/MobileCardWrapper';
import MobileWrapper from '../components/mobile/containers/MobileWrapper';
import { MARKETING_PLUS_PAGE, MARKETING_PLUS_PAGE_CONTENT } from '../constants/ErrorBoundaryNames';
import { getMobileMerchCardsToShow } from '../constants/merchCards/MerchCardsToShow';
import { NavItemEnum } from '../constants/routing/NavItems';
import { SCREENS } from '../constants/tracking/Screens';
import { MARKETING_PLUS_CARD, MARKETING_PLUS_PAGE_FEATURE_TABLE } from '../constants/tracking/TrackingSources';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useRedirectIfIneligible } from '../hooks/useRedirectIfIneligible';
import { useAppSelector } from '../reducers';
import { isUngatedForInbound25Selector, isUngatedForMarketingPlusSelector } from '../selectors/GatingSelectors';
import { bundledProductLinesForApiNameSelector } from '../selectors/ProductConfigsSelectors';
import { bundleProductsForBundleTypeSelector } from '../selectors/ProductsSelectors';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtils';
import MissingInfoFallbackPage from './MissingInfoFallbackPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MobileMarketingPlusPage = () => {
  useAnchorLinkScroll();
  useDocumentTitle('marketing-plus');
  const isAppReady = useGlobalLoading();
  const productLevels = getMobileMerchCardsToShow(MARKETING_PLUS_SUITE);
  const [selectedProductLevel, setSelectedProductLevel] = useState(productLevels[0]);
  const screen = SCREENS.MARKETING_PLUS;
  const navItem = NavItemEnum.MARKETING_PLUS;
  const isUngatedForMarketingPlus = useAppSelector(isUngatedForMarketingPlusSelector);
  const isUngatedForInbound = useAppSelector(isUngatedForInbound25Selector);
  useRedirectIfIneligible({
    condition: () => !isUngatedForMarketingPlus
  });
  useEffect(() => {
    if (!isAppReady) return;
    trackPageView({
      screen
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppReady]);
  const highlightedProducts = useAppSelector(state => {
    if (!isAppReady) return [];
    return bundleProductsForBundleTypeSelector(state, {
      bundleType: MARKETING_PLUS_SUITE
    });
  });
  const productLevelToHighlightedProductMap = productLevels.reduce((acc, productLevel) => {
    var _highlightedProducts$;
    const apiName = (_highlightedProducts$ = highlightedProducts.find(product => product.productLevel === productLevel)) === null || _highlightedProducts$ === void 0 ? void 0 : _highlightedProducts$.apiName;
    if (apiName) {
      acc[productLevel] = apiName;
    }
    return acc;
  }, {});
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const productLinesFromBundle = useAppSelector(state => {
    if (!highlightedProducts.length) return [];
    return sortProductLines(bundledProductLinesForApiNameSelector(state, {
      apiName: highlightedProducts[0].apiName
    }));
  });
  if (!isAppReady) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  const apiName = productLevelToHighlightedProductMap[selectedProductLevel];
  const i18nKeys = getI18nKeyOverrideForProductCard(apiName);
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fallback: /*#__PURE__*/_jsx(MissingInfoFallbackPage, {
      navItem: navItem
    }),
    errorBoundaryName: MARKETING_PLUS_PAGE_CONTENT,
    severity: ERROR_SEVERITY.HIGH,
    metricsOptions: {
      route: navItem
    },
    children: /*#__PURE__*/_jsxs(MobileWrapper, {
      navItem: navItem,
      navMarkerName: "MARKETING_PLUS_PAGE",
      children: [/*#__PURE__*/_jsx(BannerSection, {
        screen: screen
      }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
        children: [/*#__PURE__*/_jsx(ProductPageTitle, {
          nonProductIdentifier: navItem
        }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
          nonProductIdentifier: navItem
        })]
      }), productLevelToHighlightedProductMap[selectedProductLevel] && /*#__PURE__*/_jsxs(PageSection, {
        children: [/*#__PURE__*/_jsx(MobileMerchCardSelectorButtons, {
          options: productLevels,
          onClick: setSelectedProductLevel,
          selectedOption: selectedProductLevel
        }), /*#__PURE__*/_jsx(MobileCardWrapper, {
          children: /*#__PURE__*/_jsx(ProductCard, {
            apiName: apiName,
            title: i18nKeys === null || i18nKeys === void 0 ? void 0 : i18nKeys.title,
            description: i18nKeys === null || i18nKeys === void 0 ? void 0 : i18nKeys.description,
            screen: screen,
            columnStart: 1,
            trackingSource: MARKETING_PLUS_CARD
          })
        }), isUngatedForInbound && /*#__PURE__*/_jsx(View, {
          marginBlock: "800",
          children: /*#__PURE__*/_jsx(CreditsCallout, {
            navItem: screen
          })
        }), /*#__PURE__*/_jsx(MobileBundleFeatureTable, {
          apiNames: highlightedProducts.map(({
            apiName: highlightedProduct
          }) => highlightedProduct),
          productLevel: selectedProductLevel,
          productLinesFromBundle: productLinesFromBundle,
          screen: screen,
          trackingSource: MARKETING_PLUS_PAGE_FEATURE_TABLE
        })]
      }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(PhoneSupportFooter, {
        phoneSourceId: "marketingplus-footer",
        screen: screen,
        upgradeProduct: BUNDLE
      })]
    })
  });
};
export default withErrorBoundary(MobileMarketingPlusPage, {
  fallback: /*#__PURE__*/_jsx(Redirect, {
    to: createToObject('/suite')
  }),
  errorBoundaryName: MARKETING_PLUS_PAGE,
  severity: ERROR_SEVERITY.HIGH
});